// @flow
import React, { Component } from 'react';
import i18n from 'app/common/lib/i18n';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import { Box, Link, Spinner, Text } from 'gestalt';

const numberToPixel = (value) => value + 'px';

type Props = {|
  signupDelay: number, // seconds
  handleSignup: () => void,
  handleLogin: () => void,
  loggingId: string,
|};

type State = {|
  animationMinHeight: number,
  showRegisterRequestLoading: boolean,
|};

export class DelayedSignupConfirmationDialog extends Component<Props, State> {
  state: State = {
    animationMinHeight: -1, // used to animate transition from default render state height to a shortened height
    showRegisterRequestLoading: false,
  };

  componentDidMount() {
    const delayInMilliseconds = this.props.signupDelay * 1000; // Convert seconds to milliseconds
    this.delayedSignupTimeoutId = window.setTimeout(() => {
      trackRegisterAction(
        `unauth.google_one_tap.confirmation_dialog.${
          this.props.loggingId
        }.signup_delay_timer_reached`,
      );
      this.requestAnimationFrameId = window.requestAnimationFrame(this.mountStyle);
      this.props.handleSignup();
    }, delayInMilliseconds);
  }

  componentWillUnmount() {
    this.clearDelayedSignupTimeout();
    this.cancelAnimationFrame();
  }

  mountStyle = () => this.setState({ showRegisterRequestLoading: true });

  requestAnimationFrameId = null;

  clearDelayedSignupTimeout() {
    if (this.delayedSignupTimeoutId) {
      window.clearTimeout(this.delayedSignupTimeoutId);
    }
  }

  cancelAnimationFrame() {
    if (this.requestAnimationFrameId) {
      window.cancelAnimationFrame(this.requestAnimationFrameId);
    }
  }

  delayedSignupTimeoutId: number;

  handleLoginButtonClick = ({ event }: { event: SyntheticMouseEvent<> }) => {
    trackRegisterAction(
      `unauth.google_one_tap.confirmation_dialog.${this.props.loggingId}.login_click`,
    );
    this.clearDelayedSignupTimeout();

    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.props.handleLogin();
  };

  render() {
    const { animationMinHeight, showRegisterRequestLoading } = this.state;
    const style = {
      transitionProperty: 'min-height',
      transitionDuration: '500ms',
      transitionTimingFunction: 'ease-in-out',
    };

    if (animationMinHeight >= 0) {
      let newAnimationMinHeight = animationMinHeight;

      if (showRegisterRequestLoading) {
        newAnimationMinHeight = 0;
      }
      Object.assign({}, style, { minHeight: numberToPixel(newAnimationMinHeight) });
    }

    return (
      <div
        style={style}
        ref={(c) => {
          if (c && !this.state.showRegisterRequestLoading && !(this.state.animationMinHeight > 0)) {
            this.setState({ animationMinHeight: c.clientHeight });
          }
        }}
      >
        <Box
          direction="column"
          display="flex"
          flex="grow"
          justifyContent="center"
          alignItems="center"
        >
          <Box>
            <Text align="center" size="xl" weight="bold">
              {showRegisterRequestLoading
                ? i18n._(
                    'Finishing up...',
                    'Title of a dialog telling the user that an account is almost done being created.',
                  )
                : i18n._(
                    'Creating your account...',
                    'Title of a dialog telling the user that an account is being created.',
                  )}
            </Text>
          </Box>
          <Box paddingY={4}>
            <Spinner
              accessibilityLabel={i18n._(
                'Account is being created',
                'Accessibility label for account creation loading dialog',
              )}
              show
            />
          </Box>
          {!showRegisterRequestLoading ? (
            <Box marginTop={2}>
              <Text inline align="center" size="md">
                {i18n._(
                  'Didn’t mean to create a new account?',
                  'Description text telling the user that they can log in if they did not intend to create a new account',
                )}
              </Text>
            </Box>
          ) : null}
          {!showRegisterRequestLoading ? (
            <Box>
              <Text inline align="center" size="md">
                <Link inline href="/login" onClick={this.handleLoginButtonClick}>
                  {i18n._(
                    'Click here to log in.',
                    'Login button which will show the login modal and enable the user to log in',
                  )}
                </Link>
              </Text>
            </Box>
          ) : null}
        </Box>
      </div>
    );
  }
}

export default DelayedSignupConfirmationDialog;
